import { getTransfersOfferType, numberWithCommas } from './utils';
import { clubLogoForTeam } from './hubClubLogos';
import { matchKickoff } from './localTime';

const IN_TYPES = new Set(['Buy', 'Loan In']);
/** Off-season / TW buffer before MD1 (season ~3 weeks, off-season ~3 days). */
const OFFSEASON_DAYS = 5;

export const IN_SLICE_COLORS = [
  '#00B56A',
  '#2dd4bf',
  '#34d399',
  '#10b981',
  '#14b8a6',
  '#4ade80',
  '#5eead4',
  '#86efac',
];

export const OUT_SLICE_COLORS = [
  '#e94235',
  '#f59e0b',
  '#fb7185',
  '#f97316',
  '#ef4444',
  '#fbbf24',
  '#e11d48',
  '#d97706',
];

export const feeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const formatTransferFee = (value) => {
  const n = feeNumber(value);
  if (n == null) return 'N/A';
  return `$${numberWithCommas(Math.round(n))}`;
};

export const teamLabel = (team) =>
  team?.name || team?.team_name || team?.teamName || 'Unknown club';

const directCrest = (team) => {
  if (!team || typeof team !== 'object') return null;
  const pic =
    team.symbol ||
    team.logo ||
    team.crest ||
    team.pic ||
    team.image ||
    team.badge;
  return typeof pic === 'string' && pic.trim() ? pic : null;
};

export const teamCrest = (team) => {
  const fromPayload = directCrest(team);
  if (fromPayload) return fromPayload;
  const name = teamLabel(team);
  if (name === 'Unknown club') return null;
  return clubLogoForTeam(name);
};

const byNewest = (a, b) => {
  const aTime = new Date(a?.createdAt || a?.updatedAt || 0).getTime();
  const bTime = new Date(b?.createdAt || b?.updatedAt || 0).getTime();
  return bTime - aTime;
};

const nestedFixtureMatches = (day) => {
  if (Array.isArray(day?.matches)) return day.matches;
  if (day?.home_team || day?.away_team) return [day];
  return [];
};

const fixtureDayDate = (day) =>
  day?.match_date || day?.date || nestedFixtureMatches(day)[0]?.match_date || null;

const fixtureDayTime = (day) => {
  const t = fixtureDayDate(day) ? new Date(fixtureDayDate(day)).getTime() : NaN;
  return Number.isFinite(t) ? t : 0;
};

const finiteGoals = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const fixtureDayComplete = (day) => {
  const matches = nestedFixtureMatches(day);
  if (!matches.length) return false;
  return matches.every((match) => {
    const home = finiteGoals(
      match?.home_goals ?? match?.homeGoals ?? match?.home_score ?? match?.homeScore
    );
    const away = finiteGoals(
      match?.away_goals ?? match?.awayGoals ?? match?.away_score ?? match?.awayScore
    );
    return home != null && away != null;
  });
};

const dealTime = (deal) => {
  const t = new Date(deal?.createdAt || deal?.updatedAt || 0).getTime();
  return Number.isFinite(t) && t > 0 ? t : null;
};

/**
 * Bounds for the loaded fixture calendar. When every matchday has scores
 * (after MD18), the next transfer window has started.
 */
export const fixtureSeasonBounds = (fixtureDays, { timezone } = {}) => {
  if (!Array.isArray(fixtureDays) || !fixtureDays.length) return null;
  const sorted = [...fixtureDays].sort(
    (a, b) => fixtureDayTime(a) - fixtureDayTime(b)
  );
  const firstRaw = fixtureDayDate(sorted[0]);
  const lastRaw = fixtureDayDate(sorted[sorted.length - 1]);
  if (!firstRaw || !lastRaw) return null;
  return {
    firstRaw,
    lastRaw,
    complete: sorted.every(fixtureDayComplete),
    timezone: timezone || null,
  };
};

/** Instant after which closed deals count for the current transfer season. */
export const transferSeasonStart = (bounds) => {
  if (!bounds) return null;
  const { timezone, complete, firstRaw, lastRaw } = bounds;
  if (complete) {
    const kick = matchKickoff(lastRaw, { timezone, isCup: false });
    return kick ? kick.clone().add(2, 'hours') : null;
  }
  const kick = matchKickoff(firstRaw, { timezone, isCup: false });
  return kick ? kick.clone().subtract(OFFSEASON_DAYS, 'days') : null;
};

const seasonLabels = (seasonNumber, complete) => {
  const n = Number(seasonNumber);
  const hasN = Number.isFinite(n);
  if (complete) {
    // Fixtures still belong to the season that just finished; the open TW is “this”.
    return {
      current: 'This season',
      previous: hasN ? `Season ${n}` : 'Last season',
    };
  }
  return {
    current: hasN ? `Season ${n}` : 'This season',
    previous: hasN && n > 1 ? `Season ${n - 1}` : 'Last season',
  };
};

/**
 * Split accepted closed deals into current vs previous transfer seasons.
 * After MD18 (fixtures complete), last season’s window is no longer “current”.
 */
export const groupDealsByTransferSeason = (
  closed,
  fixtureDays,
  { timezone, seasonNumber } = {}
) => {
  const deals = Array.isArray(closed) ? closed : [];
  const bounds = fixtureSeasonBounds(fixtureDays, { timezone });
  const start = transferSeasonStart(bounds);
  const labels = seasonLabels(seasonNumber, Boolean(bounds?.complete));

  if (!start) {
    return {
      bounds,
      options: [{ key: 'current', label: labels.current, count: deals.length }],
      byKey: { current: deals, previous: [] },
      defaultKey: 'current',
    };
  }

  const startMs = start.valueOf();
  const current = [];
  const previous = [];

  deals.forEach((deal) => {
    const t = dealTime(deal);
    if (t == null) {
      // Undated leftovers after MD18 belong to last season so they clear by default.
      (bounds.complete ? previous : current).push(deal);
      return;
    }
    if (t >= startMs) current.push(deal);
    else previous.push(deal);
  });

  const options = [{ key: 'current', label: labels.current, count: current.length }];
  if (previous.length) {
    options.push({
      key: 'previous',
      label: labels.previous,
      count: previous.length,
    });
  }

  return {
    bounds,
    options,
    byKey: { current, previous },
    defaultKey: 'current',
  };
};

export const mapClosedDeal = (deal, teamId, index, palette, homeClub) => {
  const type = getTransfersOfferType(
    teamId,
    deal?.sending_team?._id,
    deal?.category
  );
  const isIn = IN_TYPES.has(type);
  const from = deal?.sending_team || (!isIn ? homeClub : null);
  const to = deal?.receiving_team || (isIn ? homeClub : null);
  return {
    id: deal?._id || `${deal?.player?._id || 'p'}-${index}`,
    playerId: deal?.player?._id || null,
    playerName: deal?.player?.name || 'Unknown player',
    type,
    direction: isIn ? 'in' : 'out',
    fromId: from?._id || null,
    toId: to?._id || null,
    fromName: teamLabel(from),
    toName: teamLabel(to),
    fromCrest: teamCrest(from),
    toCrest: teamCrest(to),
    fee: feeNumber(deal?.transfer_fee_offer),
    feeLabel: formatTransferFee(deal?.transfer_fee_offer),
    color: palette[index % palette.length],
    createdAt: deal?.createdAt || deal?.updatedAt || null,
    isLoan: Number(deal?.category) === 1,
  };
};

export const splitSeasonTransfers = (closed, teamId, homeClub) => {
  const accepted = (Array.isArray(closed) ? closed : [])
    .filter((deal) => Number(deal?.status) === 1 && deal?.player && teamId != null &&
      [deal.sending_team?._id, deal.receiving_team?._id].some((id) => id != null && String(id) === String(teamId)))
    .slice()
    .sort(byNewest);

  const arrivals = [];
  const departures = [];
  accepted.forEach((deal) => {
    const type = getTransfersOfferType(
      teamId,
      deal?.sending_team?._id,
      deal?.category
    );
    if (IN_TYPES.has(type)) arrivals.push(deal);
    else departures.push(deal);
  });

  const paint = (deals, palette) =>
    deals.map((deal, index) =>
      mapClosedDeal(deal, teamId, index, palette, homeClub)
    );

  return {
    arrivals: paint(arrivals, IN_SLICE_COLORS),
    departures: paint(departures, OUT_SLICE_COLORS),
  };
};

export const totalFees = (rows) =>
  rows.reduce((sum, row) => sum + (row.fee || 0), 0);

export const sliceValue = (row, rows) => {
  const fee = row.fee || 0;
  if (fee > 0) return fee;
  const anyFee = rows.some((item) => (item.fee || 0) > 0);
  return anyFee ? 0 : 1;
};
